import React from 'react';
import { Link } from 'react-router-dom';
import './KrishparPayroll.css';

const KrishparPayroll = () => {
  return (
    <div className="payroll-page">
      {/* HERO */}
      <section className="hero">
        <div className="hero-bg-grid"></div>
        <div className="hero-blob blob1"></div>
        <div className="hero-blob blob2"></div>
        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-label">Payroll Services</div>
            <h1 className="hero-title">
              <span className="accent">Payroll</span>
              <span>Reimagined</span>
              <span className="outline">From The Inside Out.</span>
            </h1>
            <p className="hero-sub">
              We built something different. An advanced AI engine, entirely our own, that transforms how companies pay people — turning one of the most error-prone processes in business into a seamless, intelligent, compliant experience.
            </p>
            <div className="hero-cta-row">
              {/* <Link to="/contact" className="btn-primary">See Impact</Link> */}
              <a href="#how" className="btn-outline">How It Works →</a>
            </div>
          </div>

          <div className="hero-stats-payroll">
            <div className="stat-card reveal">
              <div className="stat-num">99.8<sup>%</sup></div>
              <div className="stat-label">Payroll Accuracy Rate</div>
            </div>
            <div className="stat-card reveal reveal-delay-1">
              <div className="stat-num">80<sup>%</sup></div>
              <div className="stat-label">Reduction in Processing Time</div>
            </div>
            <div className="stat-card reveal reveal-delay-2">
              <div className="stat-num">Zero</div>
              <div className="stat-label">Compliance Penalties</div>
            </div>
            <div className="stat-card featured reveal reveal-delay-3">
              <div className="stat-icon">🎯</div>
              <div>
                <div className="stat-num">100%</div>
                <div className="stat-label">Statutory Compliance Coverage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="problem-belt">
        <div className="section-tag">The Challenge</div>
        <h2>Traditional Payroll<br /><span>Creates Business Barriers</span></h2>
        <p style={{ color: 'var(--gray)', maxWidth: '580px', marginTop: '16px', fontWeight: '300', fontSize: '1rem', lineHeight: '1.8' }}>Many businesses face operational challenges with traditional payroll systems — creating inefficiencies that impact growth, compliance, and team productivity.</p>
        <div className="prob-grid">
          <div className="prob-card">
            <div className="prob-icon">⚠️</div>
            <h3>Calculation Complexities</h3>
            <p>Manual payroll processes often lead to compounding errors — overtime miscalculations, incorrect deductions, and missed variables that accumulate across pay cycles.</p>
            <div className="prob-stat">1 in 3</div>
            <div className="prob-stat-label">businesses report frequent calculation errors</div>
          </div>
          <div className="prob-card">
            <div className="prob-icon">🕐</div>
            <h3>Time Management Issues</h3>
            <p>Finance teams spend significant time on repetitive payroll tasks — time that could be better allocated to strategic business initiatives and growth planning.</p>
            <div className="prob-stat">120+ hrs</div>
            <div className="prob-stat-label">avg. time spent per quarter on payroll admin</div>
          </div>
          <div className="prob-card">
            <div className="prob-icon">⚖️</div>
            <h3>Compliance Challenges</h3>
            <p>Evolving tax laws and labour codes require constant attention. Missing regulatory updates can lead to compliance risks and potential penalties.</p>
            <div className="prob-stat">₹2–25L</div>
            <div className="prob-stat-label">average penalty per compliance issue</div>
          </div>
          <div className="prob-card">
            <div className="prob-icon">😤</div>
            <h3>Employee Satisfaction</h3>
            <p>Late payments and payroll inaccuracies can damage employee trust and morale — impacting retention and increasing recruitment costs.</p>
            <div className="prob-stat">34%</div>
            <div className="prob-stat-label">of employees consider changing jobs after payroll issues</div>
          </div>
          <div className="prob-card">
            <div className="prob-icon">🔒</div>
            <h3>Data Security Concerns</h3>
            <p>Traditional systems with limited security controls expose sensitive salary data — creating potential breach risks and compliance vulnerabilities.</p>
            <div className="prob-stat">60%</div>
            <div className="prob-stat-label">of SMEs lack adequate payroll data protection</div>
          </div>
          <div className="prob-card">
            <div className="prob-icon">�</div>
            <h3>Limited Business Insights</h3>
            <p>Legacy payroll systems provide minimal visibility into labour costs, departmental spending, or workforce analytics — limiting strategic decision-making.</p>
            <div className="prob-stat">0</div>
            <div className="prob-stat-label">actionable payroll insights in traditional systems</div>
          </div>
        </div>
      </section>

      {/* HOW WE DO IT */}
      <section className="how-section" id="how">
        <div className="section-tag">Our Solution</div>
        <h2>Meet the Engine<br />Behind <span style={{ color: 'var(--blue)' }}>Our Payroll.</span></h2>
        <p className="section-intro">We don&apos;t use third-party software and call it a service. We built our own proprietary AI application — purpose-engineered for precision, compliance, and scale.</p>
        <div className="ai-engine">
          <div className="engine-label">⚡ Krishpar Advanced AI —PeopleX HR Platform</div>
          <div className="engine-title">One Platform. Every Variable. Every Time.</div>
          <p className="engine-desc">Our internal AI application processes payroll by ingesting every relevant variable — attendance, leaves, bonuses, deductions, tax bands, statutory requirements — and executing error-free calculations in minutes. No guessing. No rounding. No human override errors.</p>
          <div className="engine-features">
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Real-time payroll computation with multi-variable logic</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Auto-updated tax and statutory compliance rules</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">AI-driven anomaly detection before processing</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Multi-entity, multi-location payroll in one run</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Predictive labour cost modelling for leadership</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Automated payslip generation and distribution</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Audit-ready reports generated instantly</div></div>
            <div className="ef-item"><div className="ef-dot"></div><div className="ef-text">Bank-grade data encryption and access controls</div></div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="payroll-process-section">
        <div className="section-tag">The Process</div>
        <h2 style={{ color: 'var(--navy)' }}>How We Run<br /><span style={{ color: 'var(--green)' }}>Your Payroll.</span></h2>
        <p className="section-intro">Our process is built to eliminate friction at every step — from data intake to employee bank accounts.</p>
        <div className="process-steps">
          <div className="step">
            <div className="payroll-step-num">01</div>
            <div className="step-title">Data Onboarding</div>
            <p>We integrate with your existing HR and attendance systems or work with structured inputs. Our AI normalises and validates all data before a single calculation begins.</p>
          </div>
          <div className="step">
            <div className="payroll-step-num">02</div>
            <div className="step-title">AI Computation</div>
            <p>Our internal application processes every payroll variable — salaries, allowances, deductions, taxes, benefits — applying current statutory rules automatically, in real time.</p>
          </div>
          <div className="step">
            <div className="payroll-step-num">03</div>
            <div className="step-title">Anomaly Review</div>
            <p>Before any output is finalised, our AI flags deviations, inconsistencies, and edge cases for human review — a double-check layer that removes risk at source.</p>
          </div>
          <div className="step">
            <div className="payroll-step-num">04</div>
            <div className="step-title">Compliance Lock</div>
            <p>Every payroll run is cross-referenced against current labour law, PF, ESI, TDS, and professional tax requirements — ensuring every submission is bulletproof.</p>
          </div>
          <div className="step">
            <div className="payroll-step-num">05</div>
            <div className="step-title">Disbursement & Reports</div>
            <p>Payslips go out, bank transfers are cleared, and your finance team receives comprehensive analytics reports — all within agreed SLA.</p>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="payroll-impact-section" id="impact">
        <div className="section-tag">Real Impact</div>
        <h2>The Shift We Deliver.</h2>
        <p className="section-intro">Here's how we measure our impact — against problems that were quietly costing you, and outcomes we deliver instead.</p>
        <div className="impact-split">
          <div className="impact-neg">
            <div className="impact-header">
              <div className="impact-badge badge-neg">Before Us</div>
              <span style={{ fontSize: '0.82rem', color: '#5a7080' }}>The Hidden Damage</span>
            </div>
            <ul className="impact-list">
              <li data-icon="❌"><div><strong>Calculation Errors Pile Up</strong>Tax miscalculations, wrong deductions, and formula errors create downstream chaos across departments and individuals.</div></li>
              <li data-icon="❌"><div><strong>Compliance Left to Chance</strong>Keeping up with changing PF, ESI, TDS rules manually is a full-time job — and mistakes carry penalties most teams can't afford.</div></li>
              <li data-icon="❌"><div><strong>Payroll Takes Weeks</strong>Multi-step manual workflows mean payroll cycles drag on, creating bottlenecks and leadership anxiety every month.</div></li>
              <li data-icon="❌"><div><strong>Zero Analytical Visibility</strong>No real-time insight into labour costs, trends, or projections — leaving leadership to make expensive decisions with no data.</div></li>
              <li data-icon="❌"><div><strong>Employee Confidence Erodes</strong>Errors on payslips — even minor ones — quietly chip away at the trust employees place in an organisation.</div></li>
              <li data-icon="❌"><div><strong>Audit Risk Grows Silently</strong>Inconsistent records, manual overrides, and undocumented decisions build into a compliance liability that surfaces at the worst time.</div></li>
            </ul>
          </div>
          <div className="impact-pos">
            <div className="impact-header">
              <div className="impact-badge badge-pos">With Us</div>
              <span style={{ fontSize: '0.82rem', color: '#5a7080' }}>The New Standard</span>
            </div>
            <ul className="impact-list">
              <li data-icon="✅"><div><strong>99.8% Calculation Accuracy</strong>Our AI processes every variable with precision — no rounding, no overrides, no spreadsheet fatigue. Numbers are right, every time.</div></li>
              <li data-icon="✅"><div><strong>Auto-Compliant, Always</strong>We update our AI's compliance logic with every statutory change. Your payroll is aligned with current law before you even think about it.</div></li>
              <li data-icon="✅"><div><strong>Payroll Runs in Hours</strong>What used to take your team weeks takes us hours. Faster cycles, faster disbursements, and zero end-of-month panic.</div></li>
              <li data-icon="✅"><div><strong>Live Labour Cost Intelligence</strong>Dashboards and reports that show you exactly where your payroll spend goes — with forecasts that help you plan smarter.</div></li>
              <li data-icon="✅"><div><strong>Employees Trust Numbers</strong>Clean, accurate, on-time payslips every cycle. Employees stop questioning their pay and start focusing on their work.</div></li>
              <li data-icon="✅"><div><strong>Audit-Ready at All Times</strong>Every payroll run is documented, traceable, and stored. We generate audit-ready reports in seconds — not weeks of scrambling.</div></li>
            </ul>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="metrics-belt">
        <div className="metrics-grid">
          <div className="metric">
            <div className="metric-num">99.8%</div>
            <div className="metric-label">Payroll Accuracy Delivered</div>
          </div>
          <div className="metric">
            <div className="metric-num">80%</div>
            <div className="metric-label">Reduction in Processing Time</div>
          </div>
          <div className="metric">
            <div className="metric-num">100%</div>
            <div className="metric-label">Statutory Compliance Coverage</div>
          </div>
          <div className="metric">
            <div className="metric-num">₹0</div>
            <div className="metric-label">Penalties Incurred by Our Clients</div>
          </div>
          <div className="metric">
            <div className="metric-num">3×</div>
            <div className="metric-label">Faster Payroll Cycle vs Industry Avg</div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="compliance-section">
        <div className="section-tag">Compliance Coverage</div>
        <h2>Every Regulation.<br />Already Handled.</h2>
        <p className="section-intro">Our AI is continuously updated with India's evolving payroll compliance landscape — so you're never exposed, never guessing, and never behind.</p>
        <div className="comp-grid">
          <div className="comp-card">
            <div className="comp-icon">🏛️</div>
            <h3>TDS & Income Tax</h3>
            <p>Accurate monthly TDS computation, investment declaration capture, Form 16 generation, and quarterly TDS return readiness — fully automated.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon">🔐</div>
            <h3>PF & EPF Compliance</h3>
            <p>Automated Provident Fund deductions, employer contributions, ECR generation, and UAN management — aligned with EPFO guidelines at all times.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon">🏥</div>
            <h3>ESI Management</h3>
            <p>ESI contribution computation, eligibility tracking, and return filing — all handled within our AI platform without manual intervention.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon">📋</div>
            <h3>Professional Tax</h3>
            <p>State-specific professional tax slabs are maintained in our system and applied accurately across multi-location payrolls, automatically.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon">⚖️</div>
            <h3>Labour Law Alignment</h3>
            <p>Gratuity, leave encashment, bonus calculations, and statutory minimum wage compliance — handled in every payroll run, every cycle.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon">📊</div>
            <h3>Regulatory Reporting</h3>
            <p>We generate all required statutory reports and filings — from Form 24Q to PT returns — structured, signed off, and submission-ready.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-strip">
        <div className="section-tag" style={{ justifyContent: 'center', marginBottom: '24px', color: 'var(--green)' }}>Get In Touch</div>
        <h2 style={{ color: 'var(--white)', textAlign: 'center' }}>Your Payroll Deserves<br />Better Than <em style={{ color: 'var(--green)' }}>Good Enough.</em></h2>
        <p style={{ color: 'var(--gray)', textAlign: 'center' }}>Let's show you what intelligent payroll looks like for your organisation.</p>
        <Link to="/contact" className="btn-primary" style={{ marginTop: '20px', textAlign: 'center' }}>Talk to Our Team →</Link>
      </div>
    </div>
  );
};

export default KrishparPayroll;